import { isBoolean, isUndefined } from "lodash";
import { FC, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import Toggle from "@/components/elements/Toggle";
import { InputError } from "@/components/primitives/InputError";
import { InputErrorIndicator } from "@/components/primitives/InputErrorIndicator";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useUnmountEffect } from "@/hooks/useUnmountEffect";
import { FormType } from "@/integrations/forms";

import FormFactory from "./FormFactory";

export const SubForm: FC<{
  form: Extract<FormType, { type: "sub_form" }>;
  editMode?: boolean;
}> = ({ form, editMode }) => {
  const [visible, setVisible] = useState<boolean>(
    form.required || Boolean(form.value),
  );

  const { trigger, register, unregister, getValues, setValue } =
    useFormContext();
  const handleToggle = useCallback(
    async (value: boolean) => {
      if (form.name) {
        setValue(form.name, value);
      }
      setVisible(value);
      await trigger();
      if (!value) {
        form.fields.forEach((field) => {
          unregister(field.name);
        });
      }
    },
    [form, trigger, unregister, setValue],
  );

  useMountEffect(() => {
    if (!form.required && form.name) {
      const currentValue = getValues(form.name);
      if (isUndefined(currentValue)) {
        register(form.name, { value: Boolean(form.value) });
        return;
      }
      if (isBoolean(currentValue)) {
        setVisible(currentValue);
        return;
      }
    }
  });

  useUnmountEffect(() => {
    if (Array.isArray(form.fields)) {
      form.fields.forEach((field) => {
        unregister(field.name);
      });
    }
    if (!form.required) {
      unregister(form.name);
    }
  });

  if (!form.fields?.length) {
    return null;
  }

  return (
    <div className="space-y-4">
      {(!form.required || form.value) && (
        <label className="flex items-center space-x-4 border-t py-8 dark:border-gray-700">
          <Toggle checked={visible} onChange={handleToggle} />
          <span className="text-sm font-medium">{form.label}</span>
        </label>
      )}
      {form.required && !form.value && (
        <span className="text-sm font-medium">{form.label}</span>
      )}
      {visible &&
        form.fields?.map((f, i) => (
          <FormFactory key={i} form={f} editMode={editMode} />
        ))}
      {!!form.error && (
        <div className="flex flex-row items-center gap-1">
          <InputErrorIndicator />
          <InputError>{form.error}</InputError>
        </div>
      )}
    </div>
  );
};
